import { Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * One breadcrumb step. Static steps come from route `data.breadcrumb` and use a translation key;
 * dynamic steps (course title, category) are set by the page with bilingual data text.
 */
export interface BreadcrumbItem {
  labelKey?: string;
  text?: string;
  textAr?: string;
  /** Router URL; omitted for steps that have no page of their own. The last step is never a link. */
  url?: string;
}

/** The subset of `ActivatedRouteSnapshot` the service reads (kept structural so specs need no TestBed). */
export interface BreadcrumbRouteNode {
  routeConfig?: { data?: { breadcrumb?: BreadcrumbItem[] } } | null;
  firstChild?: BreadcrumbRouteNode | null;
}

export const BREADCRUMB_HOME: BreadcrumbItem = { labelKey: 'unifiedShell.breadcrumb.home', url: '/home' };

/**
 * Collects the `breadcrumb` arrays declared on each matched route (root to leaf) in order. Only the route's own
 * `routeConfig.data` is read, so data inherited by componentless children is not counted twice.
 */
export function collectRouteBreadcrumbs(root: BreadcrumbRouteNode | null | undefined): BreadcrumbItem[] {
  const items: BreadcrumbItem[] = [];
  let node = root;
  while (node) {
    const own = node.routeConfig?.data?.breadcrumb;
    if (Array.isArray(own)) items.push(...own);
    node = node.firstChild;
  }
  return items;
}

/**
 * Builds the rendered trail: Home first, then the route steps, then the dynamic steps set by the page.
 * A route without breadcrumb data yields an empty trail (nothing is rendered rather than a wrong hierarchy).
 */
export function buildBreadcrumbTrail(routeItems: BreadcrumbItem[], dynamicItems: BreadcrumbItem[] = []): BreadcrumbItem[] {
  const steps = [...(routeItems ?? []), ...(dynamicItems ?? [])].filter((item) => !isHome(item));
  if (steps.length === 0) return [];
  return [BREADCRUMB_HOME, ...steps];
}

function isHome(item: BreadcrumbItem): boolean {
  return !item || item.labelKey === BREADCRUMB_HOME.labelKey || item.url === BREADCRUMB_HOME.url;
}

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private routeItems: BreadcrumbItem[] = [];
  private dynamicItems: BreadcrumbItem[] = [];
  private readonly trailSubject = new BehaviorSubject<BreadcrumbItem[]>([]);

  readonly trail$: Observable<BreadcrumbItem[]> = this.trailSubject.asObservable();

  constructor(private router: Router) {
    // Dynamic steps belong to the page that set them: drop them as soon as a new navigation starts, so a page
    // that sets them after its data loads (always after NavigationEnd) is never overwritten.
    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart || event instanceof NavigationEnd))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.dynamicItems = [];
          return;
        }
        this.updateFromRoute(this.router.routerState?.snapshot?.root as BreadcrumbRouteNode);
      });
    // The service may be created after the first navigation has already ended.
    this.updateFromRoute(this.router.routerState?.snapshot?.root as BreadcrumbRouteNode);
  }

  get trail(): BreadcrumbItem[] {
    return this.trailSubject.value;
  }

  /** Reads the route steps from the given snapshot tree and republishes the trail. */
  updateFromRoute(root: BreadcrumbRouteNode | null | undefined): void {
    this.routeItems = collectRouteBreadcrumbs(root);
    this.publish();
  }

  /** Sets the page's dynamic steps (appended after the route steps), e.g. category and course title. */
  set(items: BreadcrumbItem[]): void {
    this.dynamicItems = [...(items ?? [])];
    this.publish();
  }

  /** Removes the dynamic steps, keeping the route steps. */
  clear(): void {
    this.dynamicItems = [];
    this.publish();
  }

  private publish(): void {
    this.trailSubject.next(buildBreadcrumbTrail(this.routeItems, this.dynamicItems));
  }
}
